import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { queueProgress, newClientId } from "@/lib/progressQueue";
import { Award, Loader2, CheckCircle2, ArrowRight, Lock, LogIn, Mail, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { SUBSCRIPTIONS_ENABLED } from "@/lib/featureFlags";

/* Drop-in "Save score" block for any simulator debrief.
   Behaviour by user state:
     - Signed-in, email verified, has access → "Save score" button
     - Signed-in, email NOT verified → "Verify email to save" + Resend button
     - Signed-in, free-tier on a Pro sim (server 402) → paywall upgrade CTA
     - Anonymous → "Sign in to save" CTA */
export function SaveSession({ simulatorId, simulatorName, score, grade, summary }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [queued, setQueued] = useState(false);
  const [paywalled, setPaywalled] = useState(false);
  const [verifyBlocked, setVerifyBlocked] = useState(false);
  const [resending, setResending] = useState(false);
  // Stable per-debrief idempotency key. Generated once when this component
  // mounts (once per debrief screen) and reused for every retry of the same
  // save — so a fast double-click, or a queued flush retry after a network
  // blip, all deduplicate to the SAME record server-side.
  const clientIdRef = useRef(newClientId());

  const isAnon = !user;

  const save = async () => {
    setSaving(true);
    const payload = {
      simulator_id: simulatorId,
      simulator_name: simulatorName,
      score: Math.round(score),
      grade: String(grade),
      summary: summary || null,
      client_id: clientIdRef.current,
    };
    try {
      await api.post("/api/progress", payload);
      setSaved(true);
      toast.success("Score saved to your training record");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      // Soft email verification — server returns 403 with code=email_verification_required
      if (status === 403 && code === "email_verification_required") {
        setVerifyBlocked(true);
        toast.error("Verify your email to save your scores", {
          description: "Check your inbox for the verification link.",
        });
      } else if (status === 402) {
        // Only reachable when SUBSCRIPTIONS_ENABLED is true — server bypasses
        // the paywall otherwise. Keep the branch so flipping the flag back on
        // just works without re-editing this component.
        setPaywalled(true);
        toast.error("Upgrade to Pro to save this score", {
          action: SUBSCRIPTIONS_ENABLED
            ? { label: "See plans", onClick: () => navigate("/pricing") }
            : undefined,
        });
      } else if (status == null) {
        // No server response — the browser is offline, DNS failed, CORS blocked,
        // or the backend is down. Queue the save locally so the learner's score
        // isn't lost. Background sync will POST it on the next `online` event.
        queueProgress(payload);
        setQueued(true);
        toast.success("Score saved locally", {
          description: "It will sync automatically when you're back online.",
        });
      } else {
        toast.error("Could not save this score. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/api/auth/resend-verification");
      toast.success("Verification email sent — check your inbox.");
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many resend attempts — try again in a minute."
        : "Couldn't send the verification email. Please try again shortly.";
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="mt-4 rounded-lg border border-[#0047AB]/20 bg-[#0047AB]/5 p-4 flex flex-wrap items-center justify-between gap-3"
      data-testid="save-session"
    >
      <div className="flex items-center gap-2 text-sm text-slate-700">
        <Award className="w-4 h-4 text-[#0047AB]" />
        {saved ? (
          <span className="flex items-center gap-1.5 font-semibold text-emerald-700" data-testid="save-session-confirm">
            <CheckCircle2 className="w-4 h-4" /> Saved — score {Math.round(score)} · grade {String(grade)}
          </span>
        ) : queued ? (
          <span className="flex items-center gap-1.5 font-semibold text-amber-800" data-testid="save-session-queued">
            <CloudOff className="w-4 h-4" /> Saved locally — score {Math.round(score)} · grade {String(grade)}. Will sync when online.
          </span>
        ) : verifyBlocked ? (
          <span className="flex items-center gap-1.5 font-semibold text-amber-800" data-testid="save-session-verify-required">
            <Mail className="w-4 h-4" /> Verify your email address to save your scores.
          </span>
        ) : paywalled ? (
          <span className="flex items-center gap-1.5 font-semibold text-amber-800" data-testid="save-session-paywalled">
            <Lock className="w-4 h-4" /> This simulator is Pro — upgrade to save your score.
          </span>
        ) : isAnon ? (
          <span data-testid="save-session-anon">
            Create a free account to save this score to your training record.
          </span>
        ) : (
          <span>Save this result to your training record.</span>
        )}
      </div>

      {saved ? (
        <Link to="/dashboard" data-testid="view-scores-btn">
          <Button variant="outline" className="text-[#0047AB] border-[#0047AB]/30">
            View my scores <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </Link>
      ) : queued ? (
        <span
          className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5"
          data-testid="save-session-queued-badge"
        >
          <CloudOff className="w-3.5 h-3.5" /> Pending sync
        </span>
      ) : verifyBlocked ? (
        <Button
          onClick={resend}
          disabled={resending}
          data-testid="save-session-resend-btn"
          className="bg-[#0047AB] hover:bg-[#003380] text-white"
        >
          {resending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
          Resend verification
        </Button>
      ) : paywalled ? (
        <Link to="/pricing" data-testid="save-session-upgrade-btn">
          <Button className="bg-[#0047AB] hover:bg-[#003380] text-white">
            <Lock className="w-4 h-4 mr-1.5" /> Upgrade to Pro
          </Button>
        </Link>
      ) : isAnon ? (
        <Button
          onClick={() => navigate(`/login?next=${encodeURIComponent(location.pathname)}`)}
          data-testid="save-session-signin-btn"
          className="bg-[#0047AB] hover:bg-[#003380] text-white"
        >
          <LogIn className="w-4 h-4 mr-1.5" /> Sign in to save
        </Button>
      ) : (
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-session-btn"
          className="bg-[#0047AB] hover:bg-[#003380] text-white"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Award className="w-4 h-4 mr-1.5" />
          )}
          Save score
        </Button>
      )}
    </div>
  );
}

export default SaveSession;
